import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Empty, Spin } from 'antd'
import { useHistory } from 'react-router-dom'
import { getMyVibeProjectPermissionRequestCount } from '../../../request'
import VibeUploadAuthorizerDialog from '../../Profile/VibeUploadAuthorizerDialog'
import VibeProjectAuthorizationDialog from './VibeProjectAuthorizationDialog'
import VibeProjectPermissionApplyDialog from './VibeProjectPermissionApplyDialog'
import VibeProjectPermissionRequestDialog from './VibeProjectPermissionRequestDialog'
import { hasAllPermissions, isTrue } from './operationPermissions'

const PAGE_ACCESS_PATH = '/portal/vibe-access'

// 详情接口新增的权限/配置字段：只在后端确实下发时才透传给弹窗，
// 缺失（老后端未部署）时由 operationPermissions 内的降级逻辑兜底。
const PERMISSION_FIELD_KEYS = [
  'mysqlDatabaseName',
  'apiConfigured',
  'hasDatabasePermission',
  'hasApiPermission',
  'hasCodePermission',
  'hasPendingRequest',
  'hasPermission'
]

/**
 * 详情「操作」tab：锚点导航 + 授权管理 / 权限申请 / 待我审批三个分区。
 * 权限标签全部来自详情接口：isOwner（项目登记人）、isDataOwner（上传人）、canManage。
 * 写操作后端仍会二次校验 Owner，这里的隐藏只是体验优化。
 */
export default function OperationsPanel ({ row, detail, onUpdated }) {
  const history = useHistory()
  const [authorizationVisible, setAuthorizationVisible] = useState(false)
  const [uploaderVisible, setUploaderVisible] = useState(false)
  const [applyVisible, setApplyVisible] = useState(false)
  const [requestVisible, setRequestVisible] = useState(false)
  // 提交申请成功后先本地标记「申请中」；下次详情刷新时以后端 hasPendingRequest 为准。
  const [requestSubmitted, setRequestSubmitted] = useState(false)
  const [pendingCount, setPendingCount] = useState({ loading: false, error: '', count: 0 })
  const mountedRef = useRef(true)
  const authorizationRef = useRef(null)
  const applyRef = useRef(null)
  const approvalRef = useRef(null)

  const source = detail || {}
  const projectId = source.projectId !== undefined && source.projectId !== null
    ? source.projectId
    : (row && row.projectId)
  const hasProjectId = projectId !== undefined && projectId !== null && projectId !== ''
  const projectName = source.projectName || (row && row.unitName) || ''
  const projectKind = source.projectKind || (row && row.projectKind) || 'dataset'
  const dataOwner = source.dataOwner || (row && row.dataOwner) || ''
  const sensitivityLevel = source.sensitivityLevel || (row && row.sensitivityLevel)
  const isOwner = Boolean(source.isOwner)
  const isDataOwner = Boolean(source.isDataOwner)
  const canManage = Boolean(source.canManage)
  // 「申请中」优先读详情接口的 hasPendingRequest；提交成功后先本地标记，等 onUpdated 刷新。
  const hasPendingRequest = Boolean(source.hasPendingRequest || requestSubmitted)

  const loadPendingCount = useCallback(() => {
    if (!isOwner) return
    setPendingCount({ loading: true, error: '', count: 0 })
    getMyVibeProjectPermissionRequestCount({})
      .then(res => {
        if (mountedRef.current) {
          setPendingCount({ loading: false, error: '', count: Number(res && res.pendingCount) || 0 })
        }
      })
      .catch(() => {
        if (mountedRef.current) setPendingCount({ loading: false, error: '待审批数量加载失败', count: 0 })
      })
  }, [isOwner])

  useEffect(() => {
    mountedRef.current = true
    loadPendingCount()
    return () => { mountedRef.current = false }
  }, [loadPendingCount])

  const handleDetailUpdated = useCallback(() => {
    if (onUpdated) onUpdated()
  }, [onUpdated])

  const handleApplied = useCallback(() => {
    setRequestSubmitted(true)
    if (onUpdated) onUpdated()
  }, [onUpdated])

  const handleApproved = useCallback(() => {
    loadPendingCount()
    if (onUpdated) onUpdated()
  }, [loadPendingCount, onUpdated])

  const openPageAccess = () => {
    history.push(hasProjectId ? `${PAGE_ACCESS_PATH}?projectId=${encodeURIComponent(projectId)}` : PAGE_ACCESS_PATH)
  }

  const scrollToSection = ref => {
    const node = ref && ref.current
    if (node && node.scrollIntoView) node.scrollIntoView({ block: 'start' })
  }

  if (!hasProjectId) {
    return <div className="term-panel">
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="缺少项目信息，无法加载操作项" />
    </div>
  }

  const providedPermissionFields = PERMISSION_FIELD_KEYS.reduce((result, key) => {
    if (source[key] !== undefined) result[key] = source[key]
    return result
  }, {})
  const projectRecord = {
    id: projectId,
    projectName,
    projectKind,
    dataOwner,
    sensitivityLevel,
    isOwner,
    isDataOwner,
    canManage,
    ...providedPermissionFields,
    databaseReadmeLength: source.databaseReadmeLength !== undefined && source.databaseReadmeLength !== null
      ? source.databaseReadmeLength
      : source.dataDictionaryLength
  }
  const uploaderProject = { id: projectId, projectName, dataOwner }
  // hasAllPermissions 与“是否还有可申请范围”互为反义，同一份判定只算一次。
  const allPermissionsHeld = hasAllPermissions(projectRecord)
  const hasPartialPermission = isTrue(projectRecord.hasPermission) || isTrue(projectRecord.hasDatabasePermission)
    || isTrue(projectRecord.hasApiPermission) || isTrue(projectRecord.hasCodePermission)

  const sections = [
    { key: 'authorization', label: '授权管理', ref: authorizationRef },
    ...(isOwner
      ? [{ key: 'approval', label: '待我审批', ref: approvalRef }]
      : [{ key: 'apply', label: '权限申请', ref: applyRef }])
  ]
  const roleNote = isOwner
    ? '你是项目 Owner，可管理授权与审批'
    : (isDataOwner ? '你是数据 Owner（上传人），可上传数据' : (canManage ? '你可以管理本项目' : '当前为只读视角，可提交权限申请'))

  return <div className="term-ops">
    <div className="term-ops-nav">
      {sections.map(section => <button
        key={section.key}
        type="button"
        className="term-ops-nav-btn"
        onClick={() => scrollToSection(section.ref)}
      >
        {section.label}
      </button>)}
      <span className="term-ops-nav-note">{roleNote}；后端仍会二次校验。</span>
    </div>

    <section className="term-ops-section" ref={authorizationRef}>
      <div className="term-ops-head">
        <span className="term-ops-title">授权管理</span>
        <span className="term-ops-desc">授权只影响本项目；系统默认授权按敏感级和一级部门自动维护。</span>
      </div>
      <div className="term-ops-card">
        <div className="term-ops-card-head"><span className="term-ops-card-title">数据库授权</span></div>
        <div className="term-ops-meta">给同事分配本项目的 MySQL 只读 / 读写权限。</div>
        <div className="term-ops-actions">
          {isOwner
            ? <button type="button" className="term-primary" onClick={() => setAuthorizationVisible(true)}>管理数据库授权</button>
            : <span className="term-ops-note">仅项目 Owner 可管理数据库授权，如需访问请使用「权限申请」。</span>}
        </div>
      </div>
      <div className="term-ops-card">
        <div className="term-ops-card-head">
          <span className="term-ops-card-title">上传人</span>
          {isDataOwner ? <span className="term-ops-tag">你是数据 Owner（上传人）</span> : null}
        </div>
        <div className="term-ops-meta">
          当前上传人（data_owner）：<span className="term-ops-strong">{dataOwner || '未登记'}</span>
        </div>
        <div className="term-ops-actions">
          {isOwner
            ? <button type="button" className="term-primary" onClick={() => setUploaderVisible(true)}>授权上传数据</button>
            : <span className="term-ops-note">仅项目 Owner 可追加上传人。</span>}
        </div>
      </div>
      <div className="term-ops-card">
        <div className="term-ops-card-head"><span className="term-ops-card-title">页面访问权限</span></div>
        <div className="term-ops-meta">页面级访问控制（指定部门 / 人员可见）暂未内嵌，先跳转 Vibe 页面访问权限页维护。</div>
        <div className="term-ops-actions">
          <button type="button" className="term-link" onClick={openPageAccess}>打开页面访问权限</button>
        </div>
      </div>
    </section>

    {!isOwner ? <section className="term-ops-section" ref={applyRef}>
      <div className="term-ops-head">
        <span className="term-ops-title">权限申请</span>
        <span className="term-ops-desc">按项目实际配置申请数据库只读 / 读写、接口或代码仓库权限，审批通过后 7 天内有效。</span>
      </div>
      <div className="term-ops-card">
        <div className="term-ops-meta">
          项目负责人同意后会按申请范围自动授权，钉钉通知仍走原有流程。
          {hasPartialPermission && !allPermissionsHeld && !hasPendingRequest
            ? ' 你已有本项目的部分权限，可申请剩余范围。'
            : ''}
        </div>
        <div className="term-ops-actions">
          {allPermissionsHeld
            ? <React.Fragment>
              <span className="term-ops-tag term-ops-tag-ok">已有全部权限</span>
              <button type="button" className="term-ghost" disabled>申请权限</button>
            </React.Fragment>
            : hasPendingRequest
              ? <React.Fragment>
                <span className="term-ops-tag">申请中</span>
                <button type="button" className="term-ghost" disabled>申请权限</button>
              </React.Fragment>
              : <button type="button" className="term-primary" onClick={() => setApplyVisible(true)}>申请权限</button>}
        </div>
      </div>
    </section> : null}

    {isOwner ? <section className="term-ops-section" ref={approvalRef}>
      <div className="term-ops-head">
        <span className="term-ops-title">待我审批</span>
        <span className="term-ops-desc">包含你负责的全部项目的待审批申请，可单条或批量同意。</span>
      </div>
      <div className="term-ops-card">
        <div className="term-ops-meta">
          {pendingCount.loading
            ? <Spin size="small" />
            : <React.Fragment>
              <span className="term-ops-count">待审批 <b>{pendingCount.count}</b> 条</span>
            </React.Fragment>}
        </div>
        {pendingCount.error
          ? <div className="term-ops-error">
            {pendingCount.error}
            <button type="button" className="term-link term-ops-retry" onClick={loadPendingCount}>重试</button>
          </div>
          : null}
        <div className="term-ops-actions">
          <button type="button" className="term-primary" onClick={() => setRequestVisible(true)}>查看待审批</button>
        </div>
      </div>
    </section> : null}

    <VibeProjectAuthorizationDialog
      visible={authorizationVisible}
      project={projectRecord}
      onClose={() => setAuthorizationVisible(false)}
      onSaved={handleDetailUpdated}
    />
    <VibeUploadAuthorizerDialog
      visible={uploaderVisible}
      project={uploaderProject}
      onClose={() => setUploaderVisible(false)}
      onSaved={handleDetailUpdated}
    />
    <VibeProjectPermissionApplyDialog
      visible={applyVisible}
      project={projectRecord}
      onClose={() => setApplyVisible(false)}
      onSaved={handleApplied}
    />
    <VibeProjectPermissionRequestDialog
      visible={requestVisible}
      onClose={() => setRequestVisible(false)}
      onChanged={handleApproved}
    />
  </div>
}
